"use client";
/**
 * Keeps `html.dark` in sync with `mpp:theme:v1` after prefs hydrate, when
 * the user changes theme in Settings, and when the OS appearance changes
 * while the preference is System.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  THEME_KEY,
  applyThemeClass,
  persistThemePreference,
  readStoredPreference,
  resolveTheme,
  type AppTheme,
  type ThemePreference,
} from "@/lib/theme/theme";

type ThemeCtx = {
  /** Resolved light/dark the chrome and charts paint. */
  theme: AppTheme;
  /** Stored choice — System follows the computer. */
  preference: ThemePreference;
  setPreference: (p: ThemePreference) => void;
  /** Explicit Light/Dark (lab toggle). Leaves System. */
  setTheme: (t: AppTheme) => void;
};

const Ctx = createContext<ThemeCtx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    typeof window === "undefined" ? "system" : readStoredPreference(),
  );
  const [theme, setThemeState] = useState<AppTheme>(() =>
    typeof window === "undefined" ? "light" : resolveTheme(readStoredPreference()),
  );

  const applyPref = useCallback((pref: ThemePreference) => {
    const resolved = resolveTheme(pref);
    setPreferenceState(pref);
    setThemeState(resolved);
    applyThemeClass(resolved);
  }, []);

  // Re-read after mount / prefs hydrate so server-synced value wins.
  useEffect(() => {
    applyPref(readStoredPreference());
  }, [applyPref]);

  // Cross-tab / prefs-flush updates.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== THEME_KEY) return;
      applyPref(readStoredPreference());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [applyPref]);

  // Follow the computer while System is selected.
  useEffect(() => {
    if (preference !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyPref("system");
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [preference, applyPref]);

  const setPreference = useCallback(
    (p: ThemePreference) => {
      persistThemePreference(p);
      applyPref(p);
    },
    [applyPref],
  );

  const setTheme = useCallback(
    (t: AppTheme) => {
      setPreference(t);
    },
    [setPreference],
  );

  const value = useMemo(
    () => ({ theme, preference, setPreference, setTheme }),
    [theme, preference, setPreference, setTheme],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeCtx {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}
